import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import infrastructureMonitoringService from '../../../services/infrastructureMonitoringService';

const ApiPerformancePanel = ({ timeRange }) => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadApiMetrics();
  }, [timeRange, filter]);

  const loadApiMetrics = async () => {
    try {
      setLoading(true);
      const filters = { timeRange, limit: 50 };
      if (filter !== 'all') {
        filters.status = filter;
      }
      const data = await infrastructureMonitoringService?.getApiPerformanceMetrics(filters);
      setMetrics(data);
    } catch (error) {
      console.error('Error loading API metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      healthy: 'text-green-600 bg-green-50',
      degraded: 'text-yellow-600 bg-yellow-50',
      critical: 'text-red-600 bg-red-50',
      offline: 'text-gray-600 bg-gray-50'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50';
  };

  const getResponseTimeColor = (ms) => {
    if (ms < 200) return 'text-green-600';
    if (ms < 500) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div>
      {/* Filter Controls */}
      <div className="mb-6 flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
        <div className="flex gap-2">
          {['all', 'healthy', 'degraded', 'critical']?.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`
                px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${filter === status
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader" className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      ) : metrics?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Activity" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No API performance data available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recorded At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metrics?.map((metric) => {
                const successRate = metric?.requestCount > 0
                  ? ((metric?.successCount / metric?.requestCount) * 100)?.toFixed(1)
                  : 0;

                return (
                  <tr key={metric?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{metric?.endpointName}</div>
                        <div className="text-xs text-gray-500">{metric?.endpointPath}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
                        {metric?.httpMethod}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold ${getResponseTimeColor(metric?.responseTimeMs)}`}>
                        {metric?.responseTimeMs}ms
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(metric?.endpointStatus)}`}>
                        {metric?.endpointStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {metric?.requestCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 w-16">
                          <div
                            className={`h-2 rounded-full ${
                              successRate >= 95 ? 'bg-green-600' : successRate >= 90 ? 'bg-yellow-600' : 'bg-red-600'
                            }`}
                            style={{ width: `${successRate}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-700">{successRate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(metric?.recordedAt)?.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ApiPerformancePanel;